import * as React from "react";
import {
  useState,
  useEffect,
  useRef,
  type ChangeEvent,
  type ComponentProps,
  type MouseEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Box,
  Button,
  Divider,
  Dialog,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  ModeEditOutline as ModeEditOutlineIcon,
  DeleteForeverOutlined as DeleteForeverOutlinedIcon,
} from "@mui/icons-material";
import { getTemplateMails, deleteTemplateMail } from "../../../api";
import SnackbarCustom, {
  type SnackbarData,
} from "../../../components/SnackBarCustom";
import PopoverDelete from "../../../components/PopoverDelete";
import { formatDate } from "../../../utils/deteUtils";
import type { IdParam } from "../../../types/common";
import type {
  MailTemplateSearchTerms,
  TemplateMailItem,
} from "../../../types/mail";

type TemplateSearchColumn = {
  id: keyof TemplateMailItem | "name" | "operation";
  label: string;
  minWidth: string;
  align?: "left" | "right";
  wordBreak?: string;
  isSmallScreen: boolean;
  isUser?: boolean;
  button?: boolean;
};

type SearchResultProps = {
  searchTerms: MailTemplateSearchTerms;
};

const columns: TemplateSearchColumn[] = [
  {
    id: "title",
    label: "件名",
    minWidth: "80px",
    isSmallScreen: true,
  },
  {
    id: "content",
    label: "本文",
    minWidth: "fit-content",
    align: "left",
    wordBreak: "break-all",
    isSmallScreen: true,
  },
  {
    id: "name",
    label: "作成者",
    minWidth: "80px",
    align: "left",
    isSmallScreen: false,
    isUser: true,
  },
  {
    id: "update_at",
    label: "更新日時",
    minWidth: "152px",
    align: "left",
    isSmallScreen: false,
  },
  {
    id: "operation",
    label: "",
    minWidth: "fit-content",
    align: "left",
    button: true,
    isSmallScreen: true,
  },
];

const SearchResult = ({ searchTerms }: SearchResultProps) => {
  // export default function SearchResult() {
  const navigate = useNavigate();

  const handleNavigate = (e: MouseEvent, path: string) => {
    e.stopPropagation();
    navigate(path);
  };

  //ページネーション管理
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //メールテンプレート一覧取得※
  const [mailTemplates, setMailTemplates] = useState<TemplateMailItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();

  const lastSearchedContentRef = useRef<MailTemplateSearchTerms | null>(null);

  const getMailTemplates = async (overridePage?: number) => {
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchTerms
        : (lastSearchedContentRef.current ?? searchTerms);
    try {
      const response = await getTemplateMails({
        title: contentToUse.title ?? null,
        content: contentToUse.content ?? null,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = response.mail_templates;
      const totalCount = response.total_count;
      const status = response;
      console.log("メールテンプレート一覧取得", response);
      setMailTemplates(data);
      setTotalCount(totalCount);
      // 検索ボタンで1ページ目を指定した場合はページ状態を同期し、確定条件を更新
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = searchTerms;
      } else {
        // 初回またはページネーション時は今回使用した条件を保持
        lastSearchedContentRef.current = contentToUse;
      }
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);

  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
    } else {
      getMailTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  // 削除処理
  const [deleteTemplateId, setDeleteTemplateId] = useState<IdParam>("");
  const [deletePopoverOpen, setDeletePopoverOpen] = useState(false);
  const deleteButtonClick = (id: IdParam) => (e: MouseEvent) => {
    e.stopPropagation();
    setDeleteTemplateId(id);
    setDeletePopoverOpen(true);
  };
  const deletePopupClose = () => {
    // event.stopPropagation();
    setDeletePopoverOpen(false);
  };

  //削除処理
  const deleteTemplateMailHandler = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    try {
      const status = await deleteTemplateMail(deleteTemplateId);
      console.log("メールテンプレート削除", status);
      getMailTemplates();
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  const [selectedRow, setSelectedRow] = useState<TemplateMailItem | null>(null);
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  // const [popoverData, setPopoverData] = useState(null);

  const handleLocationCodeClick = (row: TemplateMailItem) => {
    setSelectedRow(row);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const buttonVaridate = (id?: number) => {
    return id ? (
      <>
        <Box sx={{ display: "flex", gap: "8px", height: "36px" }}>
          <IconButton
            name="editButton"
            color="primary"
            onClick={(e) =>
              handleNavigate(e, `/mail/template/registation?id=${id}`)
            }
            sx={{ pointerEvents: "auto" }}
          >
            <ModeEditOutlineIcon />
          </IconButton>
          <IconButton
            name="deleteButton"
            color="error"
            onClick={deleteButtonClick(id)}
            sx={{ pointerEvents: "auto" }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      </>
    ) : null;
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          paddingTop: "16px",
          paddingBottom: "32px",
          "@media screen and (max-width:600px)": {
            flexDirection: "column",
            alignItems: "center",
          },
        }}
      >
        <Button
          name="searchButton"
          variant="contained"
          sx={{ width: "208px" }}
          onClick={() => {
            setPage(0);
            getMailTemplates(0);
          }}
        >
          検索
        </Button>
        <Button
          name="addButton"
          variant="outlined"
          sx={{ display: "flex", width: "208px", gap: "8px" }}
          onClick={(e) => handleNavigate(e, "/mail/template/registation")}
        >
          追加
        </Button>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            gap: "16px",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              paddingLeft: "40px",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索結果
          </Box>
        </Box>
        {/* <SearchResult /> */}
        <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: "none" }}>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{
                        padding: "8px",
                        fontWeight: "bold",
                        borderBottom: 2,
                        borderBottomColor: "outline.dark",
                        "@media screen and (max-width:900px)": {
                          display:
                            column.isSmallScreen === true
                              ? "table-cell"
                              : "none",
                        },
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {mailTemplates?.map((mailTemplate, index) => (
                  <TableRow
                    hover
                    {...({
                      name: `searchResult_${index}`,
                    } as ComponentProps<typeof TableRow>)}
                    role="checkbox"
                    tabIndex={-1}
                    key={mailTemplate.id}
                    onClick={() => handleLocationCodeClick(mailTemplate)}
                    sx={{
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          sx={{
                            padding: "8px",
                            ...(column.wordBreak
                              ? { wordBreak: column.wordBreak }
                              : {}),
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "32px",
                              maxHeight: "100px",
                              overflow: "hidden",
                            }}
                          >
                            {column.id === "update_at"
                              ? formatDate(mailTemplate.update_at)
                              : column.isUser
                                ? mailTemplate.create_user.name
                                : column.id === "title" || column.id === "content"
                                  ? mailTemplate[column.id]
                                  : null}
                            {column.button
                              ? buttonVaridate(mailTemplate.id)
                              : null}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
            <Box
              sx={{
                display: "flex",
                height: "56px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "2px solid #CCC",
                position: "sticky",
                top: 0,
                backgroundColor: "#fff",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  paddingLeft: "8px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                メールテンプレート詳細
              </Box>
              <IconButton
                aria-label="close"
                onClick={handlePopoverClose}
                sx={{
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
              <TableBody>
                {selectedRow &&
                  columns.slice(0, 4).map((column) => {
                    return (
                      <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                        <TableCell
                          align={column.align}
                          sx={{
                            color: "outline.dark",
                            fontWeight: "bold",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              WebkitWritingMode: "horizontal-tb",
                              height: "100%",
                              padding: "8px",
                            }}
                          >
                            {column.label}
                          </Box>
                        </TableCell>
                        <TableCell
                          align={column.align}
                          sx={{
                            ...(column.wordBreak
                              ? { wordBreak: column.wordBreak }
                              : {}),
                            writingMode: "horizontal-tb",
                            boxSizing: "border-box",
                            width: "calc((100vw - 64px) / 2)",
                            overflowWrap: "break-word",
                          }}
                        >
                          {column.id === "update_at" ? (
                            formatDate(selectedRow.update_at)
                          ) : column.id === "name" ? (
                            <>{selectedRow.create_user.name}</>
                          ) : column.id === "title" || column.id === "content" ? (
                            selectedRow[column.id]
                          ) : null}
                          {column.button
                            ? buttonVaridate(selectedRow.id)
                            : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <Box
              sx={{
                margin: "16px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              {buttonVaridate(selectedRow?.id)}
            </Box>
          </Dialog>
          <PopoverDelete
            open={deletePopoverOpen}
            onClose={deletePopupClose}
            yesNo={deleteTemplateMailHandler}
          />
          <SnackbarCustom
            data={snackBarstatus}
            openStatus={snackbarOpen}
            onClose={() => setSnackbarOpen(false)}
          />
          <TablePagination
            rowsPerPageOptions={[10, 30, 50, 100]}
            component="div"
            //totalDataがnull、undefined時に対応
            count={totalCount ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
              {
                margin: "0 !important",
                padding: "0 !important",
              },
            }}
            labelRowsPerPage="表示件数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
          />
        </Paper>
      </Box>
    </>
  );
};
export default SearchResult;
